<template>
  <div class="calendar-wrapper">
    <h3>일정공유</h3>

      <!-- ✅ 일정 추가 버튼 (권한에 따라 표시) -->
      <el-button 
          v-if="canAddEvent(selectedTab)" 
          type="primary" 
          class="add-event-btn"
          @click="openAddEventModal()">
          + 일정 추가
    </el-button>
    <div v-if="!isMobile && canAddEvent(selectedTab)" class="add-hint">
      날짜 칸을 클릭하면 해당 날짜로 일정을 바로 추가할 수 있습니다.
    </div>

       <!-- ✅ 부서별 캘린더 탭 + 자리비움 오버레이 토글 -->
    <div class="tabs-row">
      <el-tabs v-model="selectedTab" @tab-change="fetchLeaveData" class="tabs-grow">
        <el-tab-pane
          v-for="cal in filteredCalendars"
          :key="cal.id"
          :label="cal.name"
          :name="cal.id">
        </el-tab-pane>
      </el-tabs>
      <div class="absence-toggle">
        <span class="absence-dot"></span>
        <span class="absence-label">자리비움 표시</span>
        <el-switch v-model="showAbsences" size="small" />
      </div>
    </div>

    <!-- FullCalendar -->
    <FullCalendar :options="calendarOptions" />

    <!-- 모바일: 날짜 탭 → 그날 일정/자리비움 하단 시트 -->
    <div class="mv-scrim" :class="{ on: sheetOpen }" @click="sheetOpen = false"></div>
    <div class="mv-sheet" :class="{ on: sheetOpen }">
      <div class="mv-grab"></div>
      <div class="mv-sh">
        <div class="mv-t">{{ sheetTitle }}</div>
        <div class="mv-c">{{ sheetEntries.length }}건</div>
        <el-button
          v-if="canAddEvent(selectedTab)"
          size="small" type="primary" plain
          @click="openAddEventModal(sheetDate); sheetOpen = false"
        >+ 일정 추가</el-button>
      </div>
      <div class="mv-body">
        <div v-if="!sheetEntries.length" class="mv-empty">일정이 없습니다.</div>
        <div
          v-for="ev in sheetEntries"
          :key="ev.id"
          class="mv-row"
          :class="{ readonly: ev.extendedProps?.isAbsence }"
          @click="openSheetEvent(ev)"
        >
          <span
            class="mv-dot"
            :style="{ background: ev.extendedProps?.isAbsence ? '#97c459' : (ev.backgroundColor || '#409EFF') }"
          ></span>
          <span class="mv-title">{{ ev.title }}</span>
          <span v-if="ev.extendedProps?.isAbsence" class="mv-badge">자리비움</span>
          <span v-else class="mv-arrow">›</span>
        </div>
      </div>
    </div>

   <!-- 이벤트 설명 모달 -->
   <el-dialog v-model="isModalVisible" title="상세 정보" :fullscreen="isMobile" :width="isMobile ? undefined : '480px'">
    <div style="margin-bottom:14px">
      <strong>제목</strong>
      <div style="margin-top:4px">{{ selectedEvent?.title }}</div>
    </div>

    <div style="margin-bottom:14px">
      <strong>기간</strong>
      <div style="margin-top:4px">{{ selectedEvent?.start }} ~ {{ selectedEvent?.end }}</div>
    </div>

    <div style="margin-bottom:14px">
      <strong>설명</strong>
      <div style="margin-top:4px">
        <!-- eslint-disable-next-line vue/no-v-html -- DOMPurify로 새니타이징된 값만 바인딩됨 (utils/sanitizeHtml.js) -->
        <p v-html="sanitizeHtml(formattedDescription)" class="event-description"></p>
      </div>
    </div>

    <template #footer>
      <div :style="isMobile ? 'display:flex;gap:8px' : ''">
        <el-button v-if="isScheduler" type="primary" @click="openEditEventModal" :style="isMobile ? 'flex:1;margin:0' : ''">수정</el-button>
        <el-button @click="isModalVisible = false" :style="isMobile ? 'flex:1;margin:0' : ''">닫기</el-button>
      </div>
    </template>
  </el-dialog>
    <!-- 이벤트 추가 모달 -->

     <!-- 이벤트 추가 모달 -->
  <el-dialog v-model="isAddEventModalVisible" title="새 일정 추가" :fullscreen="isMobile" :width="isMobile ? undefined : '480px'">
    <el-form ref="eventFormRef" :model="newEvent" label-position="top">

      <el-form-item label="부서 선택">
        <el-select v-if="departmentCalendars.length > 0" v-model="newEvent.calendar_id" placeholder="부서 선택" style="width:100%">
          <el-option v-for="cal in departmentCalendars" :key="cal.id" :label="cal.name" :value="cal.id"></el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="제목" required>
        <el-input v-model="newEvent.title"></el-input>
      </el-form-item>

      <el-form-item label="시작 날짜" required>
        <el-date-picker v-model="newEvent.start_date" type="date" value-format="YYYY-MM-DD" placeholder="시작 날짜" style="width:100%"></el-date-picker>
      </el-form-item>

      <el-form-item label="종료 날짜" required>
        <el-date-picker v-model="newEvent.end_date" type="date" value-format="YYYY-MM-DD" placeholder="종료 날짜" style="width:100%"></el-date-picker>
      </el-form-item>

      <el-form-item label="설명">
        <el-input v-model="newEvent.description" type="textarea" :rows="3"></el-input>
      </el-form-item>

      <el-form-item label="이벤트 색상">
        <el-color-picker v-model="newEvent.color"></el-color-picker>
      </el-form-item>
    </el-form>

    <template #footer>
      <div :style="isMobile ? 'display:flex;gap:8px;flex-wrap:wrap' : ''">
        <el-button @click="isAddEventModalVisible = false" :style="isMobile ? 'flex:1;margin:0' : ''">취소</el-button>
        <el-button plain type="primary" @click="addEvent(true)" :style="isMobile ? 'flex:1;margin:0' : ''">추가 후 계속</el-button>
        <el-button type="primary" @click="addEvent(false)" :style="isMobile ? 'flex:1;margin:0' : ''">추가</el-button>
      </div>
    </template>
  </el-dialog>


    <!-- 이벤트 수정 모달 -->
     <!-- 이벤트 수정 모달 -->
  <el-dialog v-model="isEditEventModalVisible" title="일정 수정" :fullscreen="isMobile" :width="isMobile ? undefined : '480px'">
    <el-form ref="editEventFormRef" :model="editEvent" label-position="top">

      <el-form-item label="부서 선택">
        <el-select v-if="departmentCalendars.length > 0" v-model="editEvent.calendar_id" placeholder="부서 선택" style="width:100%">
          <el-option v-for="cal in departmentCalendars" :key="cal.id" :label="cal.name" :value="cal.id"></el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="제목" required>
        <el-input v-model="editEvent.title"></el-input>
      </el-form-item>

      <el-form-item label="시작 날짜" required>
        <el-date-picker v-model="editEvent.start_date" type="date" value-format="YYYY-MM-DD" placeholder="시작 날짜" style="width:100%"></el-date-picker>
      </el-form-item>

      <el-form-item label="종료 날짜" required>
        <el-date-picker v-model="editEvent.end_date" type="date" value-format="YYYY-MM-DD" placeholder="종료 날짜" style="width:100%"></el-date-picker>
      </el-form-item>

      <el-form-item label="설명">
        <el-input v-model="editEvent.description" type="textarea" :rows="3"></el-input>
      </el-form-item>

      <el-form-item label="이벤트 색상">
        <el-color-picker v-model="editEvent.color"></el-color-picker>
      </el-form-item>
    </el-form>

    <template #footer>
      <div :style="isMobile ? 'display:flex;gap:8px;flex-wrap:wrap' : ''">
        <el-button @click="isEditEventModalVisible = false" :style="isMobile ? 'flex:1;margin:0' : ''">취소</el-button>
        <el-button type="primary" @click="updateEvent" :style="isMobile ? 'flex:1;margin:0' : ''">수정 완료</el-button>
        <el-button v-if="isScheduler" type="danger" @click="deleteEvent" :style="isMobile ? 'flex:1;margin:0' : ''">삭제</el-button>
      </div>
    </template>
  </el-dialog>
  </div>
</template>


<script setup>
import { ref, watch,onMounted ,computed} from "vue";
import axiosInstance from "@/api/axios"; // Axios 인스턴스 가져오기
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import "ag-grid-community/styles/ag-grid.css"; // Ag-Grid 기본 스타일
import "ag-grid-community/styles/ag-theme-alpine.css"; // Ag-Grid 테마 스타일
import dayjs from "dayjs"; // Day.js 가져오기
import { nextTick } from "vue";
import { useUserStore } from "@/stores/user";
import { ElMessageBox, ElMessage } from "element-plus";
// FullCalendar import
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'


const userStore = useUserStore();
const isEditEventModalVisible = ref(false);
const editEvent = ref({});

// 부서 캘린더 목록 (서버에서 가져오기)
const departmentCalendars = ref([]);

// 선택된 월
const selectedMonth = ref(null);

// 캘린더 이벤트 데이터
const calendarEvents = ref([]);

const holidays = ref([]); // 기본값으로 빈 배열 설정

// 사용자 권한 확인 (일정 관리자 여부)
const isScheduler = userStore.isScheduler;
const isAddEventModalVisible = ref(false);

const isCompanyScheduler = computed(() => userStore.isCompanyScheduler);
const isDepartmentScheduler = computed(() => userStore.isDepartmentScheduler);

const selectedEvent = ref(null); // 클릭된 이벤트 데이터 저장
const isModalVisible = ref(false); // 모달 표시 여부

// ✅ "회사 전체 일정(1)"과 "사용자 부서 일정"만 필터링하여 표시
const filteredCalendars = computed(() => {
  return departmentCalendars.value.filter(
    (cal) => cal.id === 1 || cal.department_name === userStore.profile.department
  );
});

console.log("filteredCalendars==========",filteredCalendars)
const selectedTab = ref(1); // 기본 탭은 '회사 일정'

const selectedCalendar = computed(() => {
  return (
    filteredCalendars.value.find(cal => cal.id === selectedTab.value) ||
    filteredCalendars.value.find(cal => cal.id === 1) ||  // ✅ 기본값: "회사일정"
    null
  );
});
console.log("selectedCalendar==========",selectedCalendar)


// 이벤트 추가 모달 열기 (dateStr 전달 시 해당 날짜로 시작/종료일 프리필)
const openAddEventModal = (dateStr = null) => {
  if (typeof dateStr === "string" && dateStr) {
    newEvent.value.start_date = dateStr;
    newEvent.value.end_date = dateStr;
  }
  // 보고 있는 탭의 캘린더를 기본 선택
  if (!newEvent.value.calendar_id) {
    newEvent.value.calendar_id = selectedTab.value;
  }
  isAddEventModalVisible.value = true;
};


const updateEvent = async () => {
  if (!editEvent.value.id) {
    ElMessage.warning("수정할 이벤트가 없습니다.");
    return;
  }

  ElMessageBox.confirm(
    "정말 이 일정을 수정하시겠습니까?",
    "수정 확인",
    {
      confirmButtonText: "수정",
      cancelButtonText: "취소",
      type: "info",
    }
  )
    .then(async () => {
      try {

        const payload = {
          id: editEvent.value.id,
          title: editEvent.value.title,
          calendar_id: editEvent.value.calendar_id || null, // ✅ 필수 값 포함
          description: editEvent.value.description || "",
          start_date: editEvent.value.start_date,
          end_date: editEvent.value.end_date,
          color: editEvent.value.color,
        };

        console.log('수정대상:',payload)
        await axiosInstance.put(`/calendar/events/${editEvent.value.id}/`,payload);
        ElMessage.success("일정이 수정되었습니다.");

        isEditEventModalVisible.value = false;
        isModalVisible.value = false;
        fetchLeaveData(); // 일정 다시 불러오기
      } catch (error) {
        ElMessage.error("일정 수정에 실패했습니다.");
        console.error("❌ 수정 실패:", error.response?.data || error.message);
      }
    })
    .catch(() => {
      ElMessage.info("수정이 취소되었습니다.");
    });
};


const deleteEvent = async () => {
  if (!editEvent.value.id) {
    ElMessage.warning("삭제할 이벤트가 없습니다.");
    return;
  }

  ElMessageBox.confirm(
    "정말 이 일정을 삭제하시겠습니까?",
    "삭제 확인",
    {
      confirmButtonText: "삭제",
      cancelButtonText: "취소",
      type: "warning",
    }
  )
    .then(async () => {
      try {
        await axiosInstance.delete(`/calendar/events/${editEvent.value.id}/`);
        ElMessage.success("일정이 삭제되었습니다.");

        // ✅ 삭제된 이벤트만 제거 (전체 API 재호출 불필요)
        calendarEvents.value = calendarEvents.value.filter(e => e.id !== editEvent.value.id);


        isEditEventModalVisible.value = false;
        fetchLeaveData(); // 일정 다시 불러오기
      } catch (error) {
        ElMessage.error("일정 삭제에 실패했습니다.");
        console.error("❌ 삭제 실패:", error.response?.data || error.message);
      }
    })
    .catch(() => {
      ElMessage.info("삭제가 취소되었습니다.");
    });
};

// 추가할 새 이벤트 데이터
const newEvent = ref({
  event_type: "company_wide",
  calendar_id: null,
  title: "",
  start_date: "",
  end_date: "",
  description: "",
  color: "#409EFF", // 기본 색상
});

// 부서 캘린더 목록 불러오기
const fetchDepartmentCalendars = async () => {
  try {
    const response = await axiosInstance.get("/calendars/");
    departmentCalendars.value = response.data?.results || response.data || [];
    //departmentCalendars.value.unshift({ id: 0, name: "부서 전체" });

    console.log("-=------------------------------------------------------")
    console.log(departmentCalendars.value)
    console.log("-=------------------------------------------------------")
  } catch (error) {
    console.error("부서 캘린더 데이터 로드 실패:", error);
  }
};

// 이벤트 추가 함수 (keepOpen=true: 저장 후 창을 닫지 않고 연속 입력)
const addEvent = async (keepOpen = false) => {
  if (!newEvent.value.title || !newEvent.value.start_date || !newEvent.value.end_date) {
    ElMessage.warning("제목과 시작/종료 날짜를 입력하세요.");
    return;
  }
  try {
    const payload = {
      title: newEvent.value.title,
      calendar_id: newEvent.value.calendar_id , // ✅ 선택한 부서 캘린더 ID
      description: newEvent.value.description || "",
      start_date: newEvent.value.start_date,
      end_date: newEvent.value.end_date,
      color: newEvent.value.color || "#409EFF",
    };

    console.log("이벤트추가",payload)

    // ✅ 서버에 이벤트 저장 요청
    const response = await axiosInstance.post("/calendar/events/", payload);

    // ✅ 서버에서 반환한 이벤트 데이터를 달력에 반영
    const newEventData = response.data;

    calendarEvents.value.push({
      id: newEventData.id,
      title: newEventData.title,
      start: newEventData.start_date,  // 서버 응답 데이터를 직접 사용
      end: newEventData.end_date,
      backgroundColor: newEventData.color,
      borderColor: newEventData.color,      
      extendedProps: {
        description: newEventData.description,
        calendar_id:newEventData.calendar_id
      },
    });

    console.log(newEventData)

    ElMessage.success(`'${newEventData.title}' 일정이 추가되었습니다.`);

    if (keepOpen) {
      // ✅ 연속 입력: 창 유지, 부서/날짜/색상은 그대로 두고 제목·설명만 초기화
      newEvent.value.title = "";
      newEvent.value.description = "";
    } else {
      // ✅ 모달 닫기 + 전체 초기화 (반응형 유지)
      isAddEventModalVisible.value = false;
      Object.assign(newEvent.value, {
        event_type: "company_wide",
        calendar_id: null,
        title: "",
        start_date: "",
        end_date: "",
        description: "",
        color: "#409EFF",
      });
    }

    // ✅ Vue의 업데이트 후 DOM을 렌더링하도록 `nextTick()` 사용
    await nextTick();

  } catch (error) {
    console.error("이벤트 추가 실패:", error);
    ElMessage.error("일정 추가에 실패했습니다.");
  }
};


const handleEventClick = (info) => {
  // 자리비움(휴가)은 읽기 전용 — 일정 상세/수정 모달을 열지 않음
  if (info.event.extendedProps?.isAbsence) return;

  console.log(info.event)

  selectedEvent.value = {
    id: info.event.id, // 🛠 ID 추가
    calendar_id: info.event.extendedProps.calendar_id ,  // ✅ 부서 ID 추가
    title: info.event.title,
    start: info.event.startStr,
    //end: info.event.endStr ,
    end: dayjs(info.event.endStr).subtract(1, 'day').format("YYYY-MM-DD"),  // ✅ 하루 빼서 보정
    description: info.event.extendedProps.description , // ✅ extendedProps에서 가져오기,
    color: info.event.backgroundColor , // ✅ 서버에서 색상 값 받기

  };
  console.log(selectedEvent.value)
  isModalVisible.value = true; // 모달 열기
};

const openEditEventModal = () => {
  if (!selectedEvent.value) {
    console.error("❌ 수정할 이벤트가 선택되지 않았습니다.");
    return;
  }
  // 보기 모달 닫기
  isModalVisible.value = false;

  editEvent.value = {
    id: selectedEvent.value.id,
    calendar_id: selectedEvent.value.calendar_id,  // ✅ ID만 저장
    title: selectedEvent.value.title,
    start_date: selectedEvent.value.start ,  // ✅ Date 객체 변환
    end_date: selectedEvent.value.end ,  // ✅ Date 객체 변환
    description: selectedEvent.value.description ,
    color: selectedEvent.value.color , // ✅ 기본 색상 적용
  };

  console.log("🛠 수정할 이벤트 데이터:", editEvent.value);
  isEditEventModalVisible.value = true;
};


const fetchCalendarEvents = async (startDate, endDate) => {
  try {
    const params = { 
      start: dayjs(startDate).format("YYYY-MM-DD"), 
      end: dayjs(endDate).format("YYYY-MM-DD") 
    };

    params.calender_id = selectedTab.value; // ✅ 부서별 일정 필터링

    const response = await axiosInstance.get(`/calendar/events/`, { params });
    const eventData = response.data;

    // ✅ 이벤트 데이터 매핑
    calendarEvents.value = eventData.map(item => ({
      id: `${item.id}`,
      title: `${item.title}`,
      start: item.start_date,
      //end:item.end_date,
      end: dayjs(item.end_date).add(1, 'day').format("YYYY-MM-DD"),  // ✅ FullCalendar에서 인식할 수 있도록 수정

      backgroundColor: item.color,
      extendedProps: {
        description: item.description || "설명이 없습니다.",
        calendar_id: item.calendar_id || null,
      },
    }));

    console.log("📌 업데이트된 캘린더 이벤트:", calendarEvents.value);
    await nextTick();
  } catch (error) {
    console.error("❌ Error fetching calendar events:", error);
  }
};


const handleDatesSet = async (info) => {
  const endDate = dayjs(info.end).add(-1, "day"); // ✅ 마지막 날짜 포함
  await Promise.all([
    fetchCalendarEvents(info.start, endDate),
    fetchAbsences(info.start, endDate),
  ]);
};

// ===== 자리비움(승인 휴가) 오버레이 =====
// 켜면 어느 탭(회사/부서)에서든 승인 휴가가 초록 라벨로 겹쳐 표시됨.
// 프라이버시: 휴가 유형은 표시하지 않고 이름(+반차 시간대)만 노출.
const showAbsences = ref(localStorage.getItem("calendar_show_absences") !== "0");
const absenceEvents = ref([]);

watch(showAbsences, (v) => {
  localStorage.setItem("calendar_show_absences", v ? "1" : "0");
});

const fetchAbsences = async (startDate, endDate) => {
  try {
    const params = {
      start: dayjs(startDate).format("YYYY-MM-DD"),
      end: dayjs(endDate).format("YYYY-MM-DD"),
    };
    const response = await axiosInstance.get("/leave-data/", { params });
    const data = response.data?.results || response.data || [];
    absenceEvents.value = data.map((item) => ({
      id: `absence-${item.id}`,
      title:
        `${item.employee_name || item.employee}` +
        (item.session === "morning" ? " (오전)" : item.session === "afternoon" ? " (오후)" : ""),
      start: item.start_date,
      end: dayjs(item.end_date).add(1, "day").format("YYYY-MM-DD"), // FullCalendar end 보정
      allDay: true,
      backgroundColor: "#eaf3de",
      borderColor: "#c0dd97",
      textColor: "#27500a",
      editable: false,
      classNames: ["fc-absence-event"],
      extendedProps: { isAbsence: true, departmentName: item.department_name || "" },
    }));
  } catch (error) {
    console.error("❌ 자리비움(휴가) 조회 실패:", error);
  }
};



// 데이터 가져오기
const fetchLeaveData = async () => {
  if (!selectedMonth.value) return;
  const startOfMonth = dayjs(selectedMonth.value).startOf("month");
  const endOfMonth = dayjs(selectedMonth.value).endOf("month");

  await fetchCalendarEvents(startOfMonth, endOfMonth);
};

const formattedDescription = computed(() => {
  return selectedEvent.value?.description
    ? selectedEvent.value.description.replace(/\n/g, "<br>")
    : "설명이 없습니다.";
});


// ✅ 모바일 여부 (≤768px) - 캘린더 기본 뷰/동작 분기에 사용
const isMobile = ref(window.innerWidth <= 768);

// FullCalendar 옵션
const calendarOptions = ref({
          plugins: [dayGridPlugin,timeGridPlugin,interactionPlugin,listPlugin],

          // ✅ 명시적 툴바: 이전/다음/오늘 + 월↔목록 전환
          headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,listMonth',
          },
          buttonText: { today: '오늘', month: '월', list: '목록' },

          // ✅ 모바일도 월 그리드 기본 (점 표시 + 날짜 탭 → 하단 시트), 목록 뷰는 툴바로 전환
          initialView: 'dayGridMonth',
          height: 'auto',           // 컨테이너 빈 칸 늘어남 방지
          noEventsText: '등록된 일정이 없습니다',

          weekends: true,
          showNonCurrentDates :true,
          editable: !isMobile.value, // ✅ 터치 드래그 오작동 방지 (모바일은 비활성)
          dayMaxEvents: isMobile.value ? 2 : true,
          timeZone: "Asia/Seoul", // 한국 표준시로 설정
          locale: "ko", // 한국어 로케일 설정
          events: calendarEvents.value, // 캘린더 이벤트 데이터
          dayCellClassNames: (arg) => {

              const dateStr = dayjs(arg.date).format("YYYY-MM-DD");
              const isWeekend = arg.date.getDay() === 0 || arg.date.getDay() === 6; // 일요일(0), 토요일(6)
              
              const isHoliday = holidays.value.some( (holiday) => holiday.date === dateStr ); // 공휴일 여부       

              if (isWeekend) {
                  return "fc-weekend-cell"; // 주말 스타일 클래스
              }
              if (isHoliday) {
                  return "fc-holiday-cell"; // 공휴일 스타일 클래스
              }
              return "";
          },
          datesSet: handleDatesSet, // 날짜 변경 이벤트 핸들러 추가
          eventClick: handleEventClick, // ✅ 이벤트 클릭 핸들러 추가

          // ✅ 모바일: 날짜 탭 → 하단 시트 / 데스크탑: 날짜 클릭 → 해당 날짜로 일정 추가
          dateClick: (arg) => {
            const ds = dayjs(arg.date).format("YYYY-MM-DD");
            if (isMobile.value) {
              openSheet(ds);
            } else if (canAddEvent()) {
              openAddEventModal(ds);
            }
          },
          dayCellDidMount: (arg) => {
            const ds = dayjs(arg.date).format("YYYY-MM-DD");
            cellEls.set(ds, arg.el);
            paintCell(ds, arg.el);
          },
          dayCellWillUnmount: (arg) => {
            cellEls.delete(dayjs(arg.date).format("YYYY-MM-DD"));
          },

});

// ===== 모바일 월간: 이벤트 막대 대신 점(dot) + 개수 (휴가달력과 동일 패턴) =====
const cellEls = new Map(); // 'YYYY-MM-DD' -> 셀 엘리먼트
const sheetOpen = ref(false);
const sheetDate = ref(null);

// 해당 날짜에 걸치는 일정/자리비움 (FullCalendar end는 exclusive)
const dayEntries = (ds) => {
  return displayedEvents.value.filter((ev) => {
    const start = ev.start;
    const end = ev.end || dayjs(ev.start).add(1, "day").format("YYYY-MM-DD");
    return ds >= start && ds < end;
  });
};

const paintCell = (ds, el) => {
  if (!isMobile.value) return; // 데스크탑은 기존 이벤트 막대 그대로
  const frame = el.querySelector(".fc-daygrid-day-frame");
  if (!frame) return;
  frame.querySelectorAll(".mv-cell").forEach((n) => n.remove());

  const entries = dayEntries(ds);
  if (!entries.length) return;

  const box = document.createElement("div");
  box.className = "mv-cell";

  const dots = document.createElement("div");
  dots.className = "mv-dots";
  entries.slice(0, 6).forEach((ev) => {
    const i = document.createElement("i");
    i.style.background = ev.extendedProps?.isAbsence
      ? "#97c459"
      : ev.backgroundColor || "#409EFF";
    dots.appendChild(i);
  });
  box.appendChild(dots);

  if (entries.length > 6) {
    const cnt = document.createElement("div");
    cnt.className = "mv-cnt";
    cnt.textContent = `${entries.length}`;
    box.appendChild(cnt);
  }
  frame.appendChild(box);
};

const paintMonthCells = () => {
  cellEls.forEach((el, ds) => paintCell(ds, el));
};

// ===== 하단 시트 =====
const sheetEntries = computed(() => (sheetDate.value ? dayEntries(sheetDate.value) : []));
const sheetTitle = computed(() => {
  if (!sheetDate.value) return "";
  const dj = dayjs(sheetDate.value);
  return `${dj.format("M월 D일")} (${["일", "월", "화", "수", "목", "금", "토"][dj.day()]})`;
});
const openSheet = (ds) => {
  sheetDate.value = ds;
  sheetOpen.value = true;
};
const openSheetEvent = (ev) => {
  if (ev.extendedProps?.isAbsence) return; // 자리비움은 읽기 전용
  selectedEvent.value = {
    id: ev.id,
    calendar_id: ev.extendedProps?.calendar_id,
    title: ev.title,
    start: ev.start,
    end: dayjs(ev.end).subtract(1, "day").format("YYYY-MM-DD"),
    description: ev.extendedProps?.description,
    color: ev.backgroundColor,
  };
  sheetOpen.value = false;
  isModalVisible.value = true;
};








// 초기 데이터 로드
onMounted(async () => {

    await userStore.fetchUserProfile();  // ✅ 사용자 정보 불러오기
  
    console.log("userStore.profile",userStore.profile)

    await fetchDepartmentCalendars(); // 부서 캘린더 목록 불러오기

    // 기본으로 현재 월 설정
    selectedMonth.value = dayjs().startOf("month");
    fetchLeaveData();
});

const canAddEvent = () => {
    const calendar = selectedCalendar.value;
    console.log(calendar) 
    return isCompanyScheduler.value||isDepartmentScheduler.value; // 회사 일정 추가 권한
        
};

// 자리비움 범위 = 보고 있는 달력 범위: 회사 탭 → 전 직원, 부서 탭 → 해당 부서원만
const visibleAbsences = computed(() => {
  if (selectedTab.value === 1) return absenceEvents.value; // 회사 일정: 전 직원
  const deptName = selectedCalendar.value?.department_name;
  if (!deptName) return absenceEvents.value;
  return absenceEvents.value.filter(
    (e) => e.extendedProps.departmentName === deptName
  );
});

// 일정 + (토글 ON이면) 자리비움을 합쳐 FullCalendar에 반영
const displayedEvents = computed(() =>
  showAbsences.value
    ? [...calendarEvents.value, ...visibleAbsences.value]
    : calendarEvents.value
);

watch(displayedEvents, (newEvents) => {
  calendarOptions.value.events = newEvents; // FullCalendar에 새 이벤트 설정
  nextTick(() => {
    paintMonthCells(); // 모바일 점 표시 갱신
  });
});


watch(selectedTab, (newTab) => {
  console.log("🟢 탭 변경 감지됨:", newTab);
  console.log("📌 선택된 캘린더:", selectedCalendar.value);
});

</script>

<style scoped>

.calendar-wrapper {
    width: 90%;
    height: 90%; /* 원하는 높이 설정 */
}

.add-hint {
  font-size: 0.78rem;
  color: #909399;
  margin: 4px 0 2px;
}

/* ===== 탭 + 자리비움 토글 행 ===== */
.tabs-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.tabs-grow {
  flex: 1;
  min-width: 0;
}
.absence-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.absence-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #97c459;
}
.absence-label {
  font-size: 13px;
  color: #606266;
}

/* 자리비움 이벤트: 둥근 라벨로 일정 막대와 시각적 구분 */
.calendar-wrapper :deep(.fc-absence-event) {
  border-radius: 10px;
  font-size: 0.8rem;
}
.calendar-wrapper :deep(.fc-absence-event .fc-event-title) {
  font-weight: 500;
}

/* ===== 모바일 월간: 점(dot) 셀 (휴가달력 패턴) ===== */
.calendar-wrapper :deep(.mv-cell) {
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 4px;
  pointer-events: none;
}
.calendar-wrapper :deep(.mv-dots) {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}
.calendar-wrapper :deep(.mv-dots i) {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: block;
}
.calendar-wrapper :deep(.mv-cnt) {
  position: absolute;
  right: 0;
  bottom: 0;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: #9aa6b6;
  border-radius: 8px;
  padding: 0 5px;
  line-height: 1.5;
}

/* ===== 모바일 하단 시트 ===== */
.mv-scrim {
  position: fixed;
  inset: 0;
  background: rgba(20, 28, 40, 0.45);
  opacity: 0;
  pointer-events: none;
  transition: 0.2s;
  z-index: 1900;
}
.mv-scrim.on {
  opacity: 1;
  pointer-events: auto;
}
.mv-sheet {
  position: fixed;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%) translateY(100%);
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 18px 18px 0 0;
  z-index: 2000;
  transition: transform 0.26s cubic-bezier(0.2, 0.8, 0.2, 1);
  max-height: 70vh;
  display: flex;
  flex-direction: column;
}
.mv-sheet.on {
  transform: translateX(-50%) translateY(0);
}
.mv-grab {
  width: 42px;
  height: 5px;
  background: #d7dde6;
  border-radius: 3px;
  margin: 9px auto 4px;
}
.mv-sh {
  padding: 6px 18px 12px;
  border-bottom: 1px solid #e8ecf2;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.mv-sh .mv-t {
  font-size: 16px;
  font-weight: 700;
}
.mv-sh .mv-c {
  font-size: 13px;
  color: #8a94a6;
}
.mv-body {
  overflow: auto;
  padding: 4px 0 24px;
}
.mv-empty {
  padding: 28px 0;
  text-align: center;
  color: #8a94a6;
  font-size: 14px;
}
.mv-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-bottom: 1px dashed #eef1f6;
  cursor: pointer;
}
.mv-row:last-child {
  border-bottom: none;
}
.mv-row.readonly {
  cursor: default;
}
.mv-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.mv-title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mv-badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #eaf3de;
  color: #27500a;
  flex-shrink: 0;
}
.mv-arrow {
  color: #c0c4cc;
  flex-shrink: 0;
}

.fc {
    height: 100%; /* FullCalendar 높이를 부모 컨테이너에 맞춤 */
}


.grid-chart-container {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}
.grid-container {
  flex: 1;
}



.fc-daygrid-day.fc-weekend-cell {
    background-color: #f9f9f9 !important; /* 주말 배경색 */
}

.fc-daygrid-day.fc-holiday-cell {
    background-color: #ffebcc !important; /* 공휴일 배경색 */
}

/* ===== 모바일 캘린더 최적화 (≤768px) =====
   데스크탑(>768px)은 위 규칙 그대로 유지. FullCalendar 내부 DOM은
   라이브러리가 JS로 그려 scoped 속성이 안 붙으므로 :deep() 로 타겟. */
@media (max-width: 768px) {
  /* 가로 폭 낭비 제거 (데스크탑 90% → 모바일 100%) */
  .calendar-wrapper {
    width: 100%;
    height: auto;
  }

  /* 월간: 이벤트 막대 숨김 → 점(dot) 표시로 대체 (paintCell) */
  .calendar-wrapper :deep(.fc-daygrid-day-events) {
    display: none;
  }
  .calendar-wrapper :deep(.fc-daygrid-day-frame) {
    position: relative;
    min-height: 52px;
  }

  /* 탭 + 토글: 좁은 폭에서 줄바꿈 */
  .tabs-row {
    flex-wrap: wrap;
    gap: 4px;
  }
  .absence-toggle {
    margin-left: auto;
  }

  /* 날짜 숫자 "31일" 줄바꿈 방지 + 축소 */
  .calendar-wrapper :deep(.fc-daygrid-day-number) {
    white-space: nowrap;
    font-size: 0.8rem;
    padding: 2px 4px;
  }

  /* 요일 헤더 축소 */
  .calendar-wrapper :deep(.fc-col-header-cell-cushion) {
    font-size: 0.8rem;
    padding: 4px 2px;
  }

  /* 툴바: 좁은 폭에서 줄바꿈 허용 + 컴팩트 */
  .calendar-wrapper :deep(.fc-header-toolbar) {
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
  }
  .calendar-wrapper :deep(.fc-toolbar-title) {
    font-size: 1.1rem;
  }
  .calendar-wrapper :deep(.fc .fc-button) {
    padding: 4px 8px;
    font-size: 0.8rem;
  }
}

/* ===== 목록(아젠다) 뷰 가독성 개선 (전 화면 공통) =====
   FullCalendar 내부 DOM이라 :deep() 로 타겟. */

/* 날짜 구분 헤더: 연한 배경 + 굵게 + 밑줄 제거로 한눈에 구분 */
.calendar-wrapper :deep(.fc-list-day-cushion) {
  background-color: #eef4ff;
  padding: 10px 14px;
}
.calendar-wrapper :deep(.fc-list-day-text),
.calendar-wrapper :deep(.fc-list-day-side-text) {
  font-weight: 700;
  color: #1f2d3d;
  text-decoration: none;
}

/* 이벤트 행: 여백·터치영역 확대, 제목 강조, 호버 표시 */
.calendar-wrapper :deep(.fc-list-event) {
  cursor: pointer;
}
.calendar-wrapper :deep(.fc-list-event td) {
  padding: 12px 14px;
}
.calendar-wrapper :deep(.fc-list-event:hover td) {
  background-color: #f5f7fa;
}
.calendar-wrapper :deep(.fc-list-event-title) {
  font-size: 0.95rem;
  font-weight: 500;
  color: #303133;
}

/* 반복되는 'all-day' 시간칸: 연하게·축소해 노이즈 감소 */
.calendar-wrapper :deep(.fc-list-event-time) {
  color: #b0b3b8;
  font-size: 0.8rem;
  width: 56px;
}

/* 색상 점을 조금 더 크게 */
.calendar-wrapper :deep(.fc-list-event-dot) {
  border-width: 6px;
}

/* 모바일: 글자 키우고, 반복 시간칸은 숨겨 제목 가독성 우선 */
@media (max-width: 768px) {
  .calendar-wrapper :deep(.fc-list-event-time) {
    display: none;
  }
  .calendar-wrapper :deep(.fc-list-event-title) {
    font-size: 1rem;
  }
  .calendar-wrapper :deep(.fc-list-day-text),
  .calendar-wrapper :deep(.fc-list-day-side-text) {
    font-size: 1rem;
  }
  .calendar-wrapper :deep(.fc-list-event td) {
    padding: 14px 12px;
  }
}

</style>

